import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Modal, Button, TextField } from '@mui/material';
import styles from './SystemProjectsSummary.module.scss';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CollectionView } from '@mescius/wijmo';
import { FlexGrid as PureFlexGrid } from '@mescius/wijmo.grid';
import { CellMaker } from '@mescius/wijmo.grid.cellmaker';
import { FlexGridDetailProvider } from '@mescius/wijmo.grid.detail';
import { FlexGridFilter as PureFlexGridFilter, FilterType } from '@mescius/wijmo.grid.filter';
import '@mescius/wijmo.styles/wijmo.css';

import { WjRef } from '@mescius/wijmo.react.base';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn, ICellTemplateContext } from '@mescius/wijmo.react.grid';
import { FlexGridDetail } from '@mescius/wijmo.react.grid.detail';
import { FlexGridFilter } from '@mescius/wijmo.react.grid.filter';
import { CollectionViewNavigator } from '@mescius/wijmo.react.input';

import {
  SharedProjectInfo,
  GetMySystemsAndProjectsResponse,
  ProjectInfo,
  SharedSystemInfo,
  SystemInfo,
  UpdateSystemRequest,
  UpdateProjectRequest,
} from '../../models/Api';
import SystemService from '../../services/SystemService';

import ReactTooltip from 'react-tooltip';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { toast } from 'react-toastify';
import { FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import ProjectService, { createProjectRequest } from '../../services/ProjectService';

import { BehaviorSubject } from 'rxjs';
import { CreateDialogType } from '../../pages/Dashboard/CleanDash';
import ShareSystemDialog, { ShareSystemDialogProps } from '../ShareSystemDialog/ShareSystemDialog';
import ShareProjectDialog, { ShareProjectDialogProps } from '../ShareSystemDialog/ShareProjectDialog';

export const SystemProjectsSummary = (props: {
  currentSystem: {
    id: string | undefined;
    projectName: string | undefined | null;
    projectId: string | undefined | null;
  };
  showSystemsProjectsModal: boolean;
  openAction: (type: CreateDialogType, projectId?: string | undefined | null) => void;
  closeAction: () => void;
}) => {
  const { t } = useTranslation();
  enum TabType {
    MySystems,
    MyProjects,
    SystemsShared,
    ProjectsShared,
  }

  const basicValidationSchema = yup.object().shape({
    editName: yup.string().required(t('NameFieldIsRequired')),
  });

  type RowDataType = SystemInfo | ProjectInfo | SharedSystemInfo | SharedProjectInfo;
  type EditRowDataType = {
    id: string;
    editName: string;
    editDescription: string | null | undefined;
  };

  type BehaviourSubjectType = BehaviorSubject<CrossGridStateType>;

  type CrossGridStateType = {
    mySystemsExpandedRowIndex: undefined | number;
    sharedSystemsExpandedRowIndex: undefined | number;
    deletedProjects: string[];
  };

  const [crossGridInitialState$, setCrossGridInitialState$] = useState<BehaviourSubjectType>({} as BehaviourSubjectType);

  const crossGridInitialState: CrossGridStateType = {
    mySystemsExpandedRowIndex: undefined,
    sharedSystemsExpandedRowIndex: undefined,
    deletedProjects: [],
  };

  const setCrossGridState = (data: CrossGridStateType) => {
    return crossGridInitialState$.next({ ...data });
  };

  const updateCrossGridState = (gridState: CrossGridStateType) => {
    setCrossGridState(gridState);
  };

  const getCrossGridState = () => {
    return crossGridInitialState$.value;
  };

  const actionsColumnWidth = 80;
  const [selectedRowData, setSelectedRowData] = useState({} as RowDataType);
  const [activeCollectionView, setActiveCollectionView] = useState({} as CollectionView);
  let deletedRow: RowDataType | null = null;
  const [anchorNode, setAnchorNode] = React.useState<null | SVGSVGElement>(null);
  const isContextMenuOpen = Boolean(anchorNode);
  const handleClick = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowData: RowDataType, collectionView: CollectionView) => {
    setAnchorNode(event.currentTarget);
    setSelectedRowData(rowData);
    setActiveCollectionView(collectionView);
  };

  const activeSummaryTab = new Map();
  const [selectedTab, setSelectedTab] = useState<TabType>(TabType.MySystems);
  const [tableItems, setTableItems] = useState<GetMySystemsAndProjectsResponse>({} as GetMySystemsAndProjectsResponse);
  const [mySystemPageData, setMySystemPageData] = useState<CollectionView>();
  const [myProjectPageData, setMyProjectPageData] = useState<CollectionView>();
  const [sharedSystemPageData, setSharedSystemPageData] = useState<CollectionView>();
  const [sharedProjectPageData, setSharedProjectPageData] = useState<CollectionView>();

  const mySystemsDetailProviderRef = useRef(null);
  const sharedSystemsDetailProviderRef = useRef(null);
  const myProjectsGridRef = useRef<PureFlexGrid>(null);

  const fetchSystemsAndProjects = async () => {
    const mySystemsAndProjects = await SystemService.getMySystemsAndProjects();
    setTableItems(mySystemsAndProjects as GetMySystemsAndProjectsResponse);
  };
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [projectToAdd, setProjectToAdd] = useState<null | ProjectInfo>(null);
  const [shareSystemDialogProps, setShareSystemDialogProps] = useState<ShareSystemDialogProps>();
  const [shareProjectDialogProps, setShareProjectDialogProps] = useState<ShareProjectDialogProps>();

  const onSubmit = async (values: EditRowDataType) => {
    const inSystemsTab = selectedTab === TabType.MySystems || selectedTab === TabType.SystemsShared;
    let updatedStatus = undefined;
    const updateSystemRequest: UpdateSystemRequest = {
      id: values.id,
      name: values.editName as string,
      description: values.editDescription as string | null,
      isDeleted: false,
    };
    const updateProjectRequest: UpdateProjectRequest = createProjectRequest(values);
    if (inSystemsTab) {
      updatedStatus = await SystemService.update(updateSystemRequest);
    } else {
      updatedStatus = await ProjectService.update(updateProjectRequest);
    }
    if (updatedStatus) {
      setSelectedRowData({} as RowDataType);
      setShowEditDialog(false);
      props.closeAction();
      const status = inSystemsTab ? t('SystemInformationUpdated') : t('ProjectInformationUpdated');
      toast.success(status);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: selectedRowData.id,
      editName: selectedRowData.name,
      editDescription: selectedRowData.description,
    },
    enableReinitialize: true,
    onSubmit,
    validationSchema: basicValidationSchema,
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

  useEffect(() => {
    if (props.showSystemsProjectsModal) fetchSystemsAndProjects();
    return () => setTableItems({} as GetMySystemsAndProjectsResponse);
  }, [props.showSystemsProjectsModal]);

  useEffect(() => {
    setCrossGridInitialState$(new BehaviorSubject<CrossGridStateType>(crossGridInitialState));
  }, []);

  const autoCloseDelay = 9 * 1000; // 9 seconds because RedirectAfterDelete message has seconds as part of its diction
  const Undo = ({ isDeletedItemOpen, onUndo, closeToast }: { isDeletedItemOpen: boolean; onUndo: () => void; closeToast: () => void }) => {
    const handleUndoClick = () => {
      onUndo();
      closeToast();
    };
    let deletedItemDiction = selectedTab === TabType.MySystems ? t('SystemDeleted') : t('ProjectDeleted');
    deletedItemDiction = isDeletedItemOpen ? `${deletedItemDiction}. ${t('RedirectAfterDelete', { delay: autoCloseDelay / 1000 })}` : deletedItemDiction;
    return (
      <div>
        <h4>
          {deletedItemDiction}
          <span className={styles['undo-action']} onClick={handleUndoClick}>
            {t('Undo')}
          </span>
        </h4>
      </div>
    );
  };

  const handleMenuClose = () => {
    setAnchorNode(null);
    setSelectedRowData({} as RowDataType);
    if (showEditDialog) {
      setShowEditDialog(false);
    }
  };

  const renderEditProjectSystemDialog = () => {
    return (
      <div className={styles['edit-row-data-dialog']}>
        <FormikProvider value={formik}>
          <form onSubmit={handleSubmit}>
            <div className={styles['input-wrapper']}>
              <div>
                <label>{t('Name')}</label>
                <TextField
                  name="editName"
                  placeholder={selectedTab === TabType.MySystems ? t('NameOfTheSystem') : t('NameOfTheProject')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.editName ?? ''}
                  className={styles['name-field']}
                  inputProps={{ maxLength: 256 }}
                />
              </div>
              {errors.editName && touched.editName && <p className={styles['error-text']}>{errors.editName}</p>}
              <div>
                <label>{t('Description')}</label>
                <TextField
                  id="editDescription"
                  placeholder={selectedTab === TabType.MySystems ? t('DescribeTheSystem') : t('DescribeTheProject')}
                  maxRows={4}
                  multiline
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.editDescription ?? ''}
                  className={styles['description-field']}
                  inputProps={{ maxLength: 4000 }}
                />
              </div>
            </div>
            <div className={styles['buttons-div']}>
              <Button type="submit">{t('OK')}</Button>
              <Button
                onClick={() => {
                  setSelectedRowData({} as RowDataType);
                  setShowEditDialog(false);
                }}
              >
                {t('Cancel')}
              </Button>
            </div>
          </form>
        </FormikProvider>
      </div>
    );
  };

  const renderToolTip = () => {
    return (
      <ReactTooltip id={'EditTip'} place="top" effect="solid">
        {t('EditNameAndDescription')}
      </ReactTooltip>
    );
  };

  async function handleSystemUndoDelete(systemId: string) {
    const deletedStatus = await SystemService.undoDelete(systemId as string);
    if (!deletedStatus) {
      deletedRow = null;
      toast.success(t('SystemUndeleted') as string);
    }
  }

  async function handleProjectUndoDelete(updateRequest: UpdateProjectRequest) {
    const deletedStatus = await ProjectService.undoDelete(updateRequest);
    if (!deletedStatus) {
      deletedRow = null;
      toast.success(t('ProjectUndeleted') as string);
    }
  }

  const handleDelete = async (id: string, projectName?: string) => {
    let isDeleted: boolean | undefined = false;
    if (projectName) {
      //delete project
      const updateRequest: UpdateProjectRequest = {
        projectId: id,
        name: projectName,
        isDeleted: true,
      };
      isDeleted = await ProjectService.delete(updateRequest);
    } else {
      //delete system
      isDeleted = await SystemService.delete(id as string);
    }
    deletedRow = selectedRowData;
    handleMenuClose();
    if (isDeleted) {
      const currentGridState = getCrossGridState();
      const deletedProjectNames = currentGridState.deletedProjects;
      const isDeletedItemOpen =
        selectedRowData.id === props.currentSystem.id ||
        deletedProjectNames.some((deletedProjectName) => deletedProjectName === props.currentSystem.projectName);
      const projectDeleted = projectName && deletedRow;
      toast.warn(
        <Undo
          isDeletedItemOpen={isDeletedItemOpen}
          onUndo={() => {
            if (projectName) {
              handleProjectUndoDelete({
                projectId: id,
                name: projectName,
                isDeleted: false,
              });
            } else handleSystemUndoDelete(id);
          }}
          closeToast={() => {}}
        />,
        {
          autoClose: autoCloseDelay,
          onClose: () => {
            if (deletedRow) activeCollectionView.remove(selectedRowData);

            if (projectDeleted) {
              deletedProjectNames.push(selectedRowData.name);
              updateCrossGridState({
                mySystemsExpandedRowIndex: currentGridState.mySystemsExpandedRowIndex,
                sharedSystemsExpandedRowIndex: currentGridState.sharedSystemsExpandedRowIndex,
                deletedProjects: deletedProjectNames,
              });
            }

            if (isDeletedItemOpen) {
              toast.info(t('RedirectingToLastSystem') as string, {
                autoClose: false,
                closeButton: false,
                closeOnClick: false,
              });
              window.location.href = `${process.env.REACT_APP_LEGACY_DASHBOARD_SUMMARY_URL}`;
            }
          },
        },
      );
    }
  };

  const closeShareSystemDialog = () => {
    const shareProps: ShareSystemDialogProps = {
      systemId: '',
      systemName: '',
      isModalOpen: false,
      closeAction: closeShareSystemDialog,
    };
    setShareSystemDialogProps(shareProps);
  };

  const handleShareSystem = async (id: string) => {
    const system = tableItems.systems.find((_system) => _system.id === id);
    if (!system) {
      return;
    }
    const shareProps: ShareSystemDialogProps = {
      systemId: id,
      systemName: system.name,
      isModalOpen: true,
      closeAction: closeShareSystemDialog,
    };
    setShareSystemDialogProps(shareProps);
    handleMenuClose();
  };

  const renderShareSystemDialog = () => {
    if (!shareSystemDialogProps) {
      return;
    }
    const { systemId, systemName, isModalOpen, closeAction } = shareSystemDialogProps;
    return <ShareSystemDialog systemId={systemId} systemName={systemName} isModalOpen={isModalOpen} closeAction={closeAction}></ShareSystemDialog>;
  };

  const closeShareProjectDialog = () => {
    const shareProps: ShareProjectDialogProps = {
      projectId: '',
      projectName: '',
      isModalOpen: false,
      closeAction: closeShareProjectDialog,
    };
    setShareProjectDialogProps(shareProps);
  };

  const handleShareProject = async (id: string) => {
    const project = tableItems.projects.find((_project) => _project.id === id);
    if (!project) {
      return;
    }
    const shareProps: ShareProjectDialogProps = {
      projectId: id,
      projectName: project.name,
      isModalOpen: true,
      closeAction: closeShareProjectDialog,
    };
    setShareProjectDialogProps(shareProps);
    handleMenuClose();
  };

  const renderShareProjectDialog = () => {
    if (!shareProjectDialogProps) {
      return;
    }
    const { projectId, projectName, isModalOpen, closeAction } = shareProjectDialogProps;
    return <ShareProjectDialog projectId={projectId} projectName={projectName} isModalOpen={isModalOpen} closeAction={closeAction}></ShareProjectDialog>;
  };

  const handleGridRefresh = (currentTab: TabType) => {
    const detailProviderRef = currentTab === TabType.MySystems ? mySystemsDetailProviderRef : sharedSystemsDetailProviderRef;
    const currentReference = detailProviderRef.current as unknown as WjRef<FlexGridDetailProvider>;
    const detailProviderControl = currentReference?.control;
    const currentGridState = getCrossGridState();
    const rowIndex = currentTab === TabType.MySystems ? currentGridState?.mySystemsExpandedRowIndex : currentGridState?.sharedSystemsExpandedRowIndex;
    if (rowIndex !== undefined && detailProviderControl && rowIndex < detailProviderControl.grid.rows.length) {
      detailProviderControl.showDetail(rowIndex);
      if (currentTab === TabType.MySystems) {
        updateCrossGridState({
          mySystemsExpandedRowIndex: undefined,
          sharedSystemsExpandedRowIndex: currentGridState.sharedSystemsExpandedRowIndex,
          deletedProjects: currentGridState.deletedProjects,
        });
      } else if (currentTab === TabType.SystemsShared) {
        updateCrossGridState({
          mySystemsExpandedRowIndex: currentGridState.mySystemsExpandedRowIndex,
          sharedSystemsExpandedRowIndex: undefined,
          deletedProjects: currentGridState.deletedProjects,
        });
      }
    }
  };

  const addDateProperties = (rows: RowDataType[]) => {
    return rows?.map((row) => {
      return {
        ...row,
        createdAtDate: new Date(row.createdAt),
        updatedAtDate: row.updatedAt ? new Date(row.updatedAt) : null,
      };
    });
  };
  const collectionViewOptions = {
    pageSize: 19,
    currentItem: null,
    sortDescriptions: [{ property: 'updatedAtDate', ascending: false }],
  };
  const projectNameVariable = 'SelectedProjectName';
  const filterDefinition = {
    defaultFilterType: FilterType.Both,
    filters: [
      {
        binding: 'projectName',
        type: 'value',
        uniqueValues: null,
        sortValues: true,
        maxValues: 250,
        exclusiveValueSearch: true,
        showValues: {},
      },
    ],
  };
  const showValueEntries = new Map();
  showValueEntries.set(projectNameVariable, true);
  filterDefinition.filters[0].showValues = Object.fromEntries(showValueEntries);
  const projectNameFilterDefinition = JSON.stringify(filterDefinition);

  const mySystemsFlexGridFilter = useRef<PureFlexGridFilter>();
  const initMySystemsGridFilter = (flexGridFilter: PureFlexGridFilter) => {
    mySystemsFlexGridFilter.current = flexGridFilter;
  };

  const renderMySystemsInner = () => {
    const collectionView = new CollectionView(addDateProperties(tableItems.systems), {
      ...collectionViewOptions,
    });
    setMySystemPageData(collectionView);
    return (
      <>
        <FlexGrid key={'MySystemGrid'} itemsSource={collectionView} selectionMode={'Row'} isReadOnly refreshed={() => handleGridRefresh(TabType.MySystems)}>
          <FlexGridFilter initialized={initMySystemsGridFilter} />
          <FlexGridColumn key={'MySystemsProjectName'} header={t('ColumnProject')} binding="projectName" width={215} />
          <FlexGridColumn
            key={'MySystemsSystemName'}
            header={t('ColumnSystem')}
            binding="name"
            minWidth={255}
            width="*"
            cellTemplate={CellMaker.makeLink({
              href: `${process.env.REACT_APP_LEGACY_BASE_HOST}` + '/System/System?SystemId=${item.id}',
            })}
          />
          <FlexGridColumn key={'MySystemsDescription'} header={t('ColumnDescription')} binding="description" width={240} />
          <FlexGridColumn key={'MySystemsSystemType'} header={t('ColumnSystemType')} binding="systemTypeName" width={250} />
          <FlexGridColumn key={'MySystemsTemplate'} header={t('ColumnTemplate')} binding="templateName" width={220} />
          <FlexGridColumn key={'MySystemsCreated'} header={t('ColumnCreated')} binding="createdAtDate" width={110} />
          <FlexGridColumn key={'MySystemsUpdated'} header={t('ColumnUpdated')} binding="updatedAtDate" width={120} />
          <FlexGridColumn header={t('ColumnActions')} binding="" width={actionsColumnWidth} allowSorting={false}>
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx: ICellTemplateContext) => (
                <div className={styles['action-icons-div']}>
                  <span>
                    {renderToolTip()}
                    <FontAwesomeIcon
                      icon="pen"
                      data-tip
                      data-for="EditTip"
                      onClick={() => {
                        setShowEditDialog(true);
                        if (!selectedRowData.id) setSelectedRowData(ctx.item);
                      }}
                    />
                  </span>

                  <FontAwesomeIcon icon="bars" onClick={(e) => handleClick(e, ctx.item, collectionView)} className={styles['ham-icon']} />
                </div>
              )}
            />
          </FlexGridColumn>
          <FlexGridDetail
            key={'MySystemsDetail'}
            ref={mySystemsDetailProviderRef}
            maxHeight={250}
            rowHasDetail={(row) => row.dataItem.parts && row.dataItem.parts.length > 0}
            template={(ctx: ICellTemplateContext) => {
              if (ctx.item.parts.length > 0) {
                return (
                  <FlexGrid key={'MySystemsDetailGrid'} autoGenerateColumns={false} isReadOnly itemsSource={addDateProperties(ctx.item.parts)}>
                    <FlexGridColumn key={'MySystemsPartName'} header={t('ColumnPartName')} binding="name" width={250} />
                    <FlexGridColumn key={'MySystemsPartNumber'} header={t('ColumnPartNumber')} binding="number" width={300} />
                    <FlexGridColumn key={'MySystemsPartCreated'} header={t('ColumnCreated')} binding="createdAtDate" width={150} />
                    <FlexGridColumn key={'MySystemsPartUpdated'} header={t('ColumnUpdated')} binding="updatedAtDate" width={150} />
                  </FlexGrid>
                );
              }
            }}
          />
        </FlexGrid>
      </>
    );
  };
  const renderMySystems = useMemo(() => renderMySystemsInner(), [tableItems]);

  const renderMyProjectsInner = () => {
    const collectionView = new CollectionView(addDateProperties(tableItems.projects), {
      ...collectionViewOptions,
    });
    setMyProjectPageData(collectionView);

    return (
      <FlexGrid key={'MyProjectsGrid'} ref={myProjectsGridRef} itemsSource={collectionView} selectionMode={'Row'} isReadOnly>
        <FlexGridFilter />
        <FlexGridColumn
          key={'MyProjectsName'}
          header={t('ColumnName')}
          binding="name"
          minWidth={255}
          width="*"
          cellTemplate={CellMaker.makeLink({
            click: (e, ctx) => {
              const currentGridState = getCrossGridState();
              updateCrossGridState({
                mySystemsExpandedRowIndex: undefined,
                sharedSystemsExpandedRowIndex: currentGridState.sharedSystemsExpandedRowIndex,
                deletedProjects: currentGridState.deletedProjects,
              });
              setSelectedTab(TabType.MySystems);

              // Delay applying the filter until the current tab has been actually switched to My Systems so filter is applied at the right time
              // otherwise, without the delay, it is applied to early (FlexGridFilter will be recreated upon remounting)
              setTimeout(() => {
                if (!mySystemsFlexGridFilter?.current) {
                  return;
                }
                const project = ctx.item as ProjectInfo;
                mySystemsFlexGridFilter.current.filterDefinition = projectNameFilterDefinition.replace(projectNameVariable, project.name);
                mySystemsFlexGridFilter.current.apply();
              }, 100);
            },
          })}
        />
        <FlexGridColumn key={'MyProjectsDescription'} header={t('ColumnDescription')} binding="description" width="1.5*" />
        <FlexGridColumn key={'MyProjectsCreated'} header={t('ColumnCreated')} binding="createdAtDate" width={270} />
        <FlexGridColumn key={'MyProjectsUpdated'} header={t('ColumnUpdated')} binding="updatedAtDate" width={270} />
        <FlexGridColumn key={'MyProjectsActions'} header={t('ColumnActions')} binding="" width={actionsColumnWidth} allowSorting={false}>
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx: ICellTemplateContext) => (
              <div className={styles['action-icons-div']}>
                <span>
                  {renderToolTip()}
                  <FontAwesomeIcon
                    icon="pen"
                    data-tip
                    data-for="ProjectEditTip"
                    onClick={() => {
                      setShowEditDialog(true);
                      if (!selectedRowData.id) {
                        setSelectedRowData(ctx.item);
                      }
                    }}
                  />
                </span>
                <FontAwesomeIcon icon="bars" onClick={(e) => handleClick(e, ctx.item, collectionView)} className={styles['ham-icon']} />
              </div>
            )}
          />
        </FlexGridColumn>
      </FlexGrid>
    );
  };
  const renderMyProjects = useMemo(() => renderMyProjectsInner(), [tableItems]);

  const sharedSystemsFlexGridFilter = useRef<PureFlexGridFilter>();
  const initSharedSystemsGridFilter = (flexGridFilter: PureFlexGridFilter) => {
    sharedSystemsFlexGridFilter.current = flexGridFilter;
  };

  const renderSharedSystemsInner = () => {
    const collectionView = new CollectionView(addDateProperties(tableItems.sharedSystems), {
      ...collectionViewOptions,
    });
    setSharedSystemPageData(collectionView);
    return (
      <FlexGrid
        key={'SharedSystemsGrid'}
        itemsSource={collectionView}
        selectionMode={'Row'}
        isReadOnly
        refreshed={() => handleGridRefresh(TabType.SystemsShared)}
      >
        <FlexGridFilter initialized={initSharedSystemsGridFilter} />
        <FlexGridColumn key={'SharedSystemsProject'} header={t('ColumnProject')} binding="projectName" width={220} />
        <FlexGridColumn
          key={'SharedSystemsName'}
          header={t('ColumnName')}
          binding="name"
          minWidth={255}
          width="*"
          cellTemplate={CellMaker.makeLink({
            href: `${process.env.REACT_APP_LEGACY_BASE_HOST}` + '/System/System?SystemId=${item.id}',
          })}
        />
        <FlexGridColumn key={'SharedSystemsDescription'} header={t('ColumnDescription')} binding="description" width={240} />
        <FlexGridColumn key={'SharedSystemsType'} header={t('ColumnSystemType')} binding="systemTypeName" width={250} />
        <FlexGridColumn key={'SharedSystemsCreated'} header={t('ColumnCreated')} binding="createdAtDate" width={110} />
        <FlexGridColumn key={'SharedSystemsUpdated'} header={t('ColumnUpdated')} binding="updatedAtDate" width={120} />
        <FlexGridColumn key={'SharedSystemsOwner'} header={t('ColumnOwner')} binding="owner" width={125} />
        <FlexGridColumn key={'SharedSystemsOwnerEmail'} header={t('ColumnOwnerEmail')} binding="ownerEmail" width={150} />
        <FlexGridColumn key={'SharedSystemsActions'} header={t('ColumnActions')} binding="" width={actionsColumnWidth} allowSorting={false}>
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx: ICellTemplateContext) => (
              <span>
                {renderToolTip()}
                <FontAwesomeIcon
                  icon="pen"
                  data-tip
                  data-for="EditTip"
                  onClick={() => {
                    setShowEditDialog(true);
                    if (!selectedRowData.id) {
                      setSelectedRowData(ctx.item);
                    }
                  }}
                />
              </span>
            )}
          />
        </FlexGridColumn>
        <FlexGridDetail
          key={'SharedSystemsDetail'}
          ref={sharedSystemsDetailProviderRef}
          maxHeight={250}
          rowHasDetail={(row) => row.dataItem.parts && row.dataItem.parts.length > 0}
          template={(ctx: ICellTemplateContext) => {
            if (ctx.item.parts.length > 0) {
              return (
                <FlexGrid key={'SharedSystemsDetailGrid'} autoGenerateColumns={false} isReadOnly itemsSource={addDateProperties(ctx.item.parts)}>
                  <FlexGridColumn key={'SharedSystemsPartName'} header={t('ColumnPartName')} binding="name" width={250} />
                  <FlexGridColumn key={'SharedSystemsPartNumber'} header={t('ColumnPartNumber')} binding="number" width={300} />
                  <FlexGridColumn key={'SharedSystemsPartCreated'} header={t('ColumnCreated')} binding="createdAtDate" width={150} />
                  <FlexGridColumn key={'SharedSystemsPartUpdated'} header={t('ColumnUpdated')} binding="updatedAtDate" width={150} />
                </FlexGrid>
              );
            }
          }}
        />
      </FlexGrid>
    );
  };

  const renderSharedSystems = useMemo(() => renderSharedSystemsInner(), [tableItems]);

  const renderSharedProjectsInner = () => {
    const collectionView = new CollectionView(addDateProperties(tableItems.sharedProjects), {
      ...collectionViewOptions,
    });
    setSharedProjectPageData(collectionView);
    return (
      <FlexGrid itemsSource={collectionView} selectionMode={'Row'} isReadOnly>
        <FlexGridFilter />
        <FlexGridColumn
          key={'SharedProjectsName'}
          header={t('ColumnName')}
          binding="name"
          minWidth={255}
          width="*"
          cellTemplate={CellMaker.makeLink({
            click: (e, ctx) => {
              const currentGridState = getCrossGridState();
              updateCrossGridState({
                mySystemsExpandedRowIndex: currentGridState.mySystemsExpandedRowIndex,
                sharedSystemsExpandedRowIndex: undefined,
                deletedProjects: currentGridState.deletedProjects,
              });
              setSelectedTab(TabType.SystemsShared);

              // Delay applying the filter until the current tab has been actually switched to Systems Shared so filter is applied at the right time
              // otherwise, without the delay, it is applied to early (FlexGridFilter will be recreated upon remounting)
              setTimeout(() => {
                if (!sharedSystemsFlexGridFilter?.current) {
                  return;
                }
                const project = ctx.item as SharedProjectInfo;
                sharedSystemsFlexGridFilter.current.filterDefinition = projectNameFilterDefinition.replace(projectNameVariable, project.name);
                sharedSystemsFlexGridFilter.current.apply();
              }, 100);
            },
          })}
        />
        <FlexGridColumn key={'SharedProjectsDescription'} header={t('ColumnDescription')} binding="description" width={310} />
        <FlexGridColumn key={'SharedProjectsCreated'} header={t('ColumnCreated')} binding="createdAtDate" width={220} />
        <FlexGridColumn key={'SharedProjectsUpdated'} header={t('ColumnUpdated')} binding="updatedAtDate" width={220} />
        <FlexGridColumn key={'SharedProjectsOwner'} header={t('ColumnOwner')} binding="owner" width={200} />
        <FlexGridColumn key={'SharedProjectsOwnerEmail'} header={t('ColumnOwnerEmail')} binding="ownerEmail" width={220} />
        <FlexGridColumn key={'SharedProjectsActions'} header={t('ColumnActions')} binding="" width={actionsColumnWidth} allowSorting={false}>
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx: ICellTemplateContext) => (
              <span>
                {renderToolTip()}
                <FontAwesomeIcon
                  icon="pen"
                  data-tip
                  data-for="EditTip"
                  onClick={() => {
                    setShowEditDialog(true);
                    if (!selectedRowData.id) {
                      setSelectedRowData(ctx.item);
                    }
                  }}
                />
              </span>
            )}
          />
        </FlexGridColumn>
      </FlexGrid>
    );
  };

  const renderSharedProjects = useMemo(() => renderSharedProjectsInner(), [tableItems]);

  const showPagination = (pageColectionView: CollectionView | undefined) => {
    return pageColectionView && pageColectionView.pageCount > 1;
  };

  const renderMySystemMenuItems = () => {
    const systemType = (selectedRowData as SystemInfo)?.systemTypeName;
    const hasSystemSummary = systemType && systemType !== 'Pneumatic Multi-Part System';
    return (
      systemType && (
        <Menu
          id="long-menu"
          MenuListProps={{
            'aria-labelledby': 'long-button',
          }}
          anchorEl={anchorNode}
          open={isContextMenuOpen}
          onClose={handleMenuClose}
          className={styles['menu-item-div']}
        >
          <MenuItem className={styles['menu-item']} onClick={() => handleDelete(selectedRowData.id)}>
            {t('Delete')}
          </MenuItem>
          <MenuItem className={styles['menu-item']} onClick={() => handleShareSystem(selectedRowData.id)}>
            {t('ShareSettings')}
          </MenuItem>
          {hasSystemSummary && (
            <MenuItem
              className={styles['menu-item']}
              onClick={() => {
                window.open(`${process.env.REACT_APP_LEGACY_BASE_HOST}/System/SystemSummaryReport?SystemId=${selectedRowData.id}`, '_blank');
                handleMenuClose();
              }}
            >
              {t('ViewDetailedSystemSummary')}
            </MenuItem>
          )}
        </Menu>
      )
    );
  };

  const renderMyProjectMenuItems = () => {
    return (
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorNode}
        open={isContextMenuOpen}
        onClose={handleMenuClose}
        className={styles['menu-item-div']}
      >
        <MenuItem className={styles['menu-item']} onClick={() => handleDelete(selectedRowData.id, selectedRowData.name)}>
          {t('Delete')}
        </MenuItem>
        <MenuItem className={styles['menu-item']} onClick={() => handleShareProject(selectedRowData.id)}>
          {t('ShareSettings')}
        </MenuItem>
        <MenuItem
          className={styles['menu-item']}
          onClick={() => {
            props.openAction(CreateDialogType.System, selectedRowData.id);
            handleMenuClose();
          }}
        >
          {t('CreateSystem')}
        </MenuItem>
      </Menu>
    );
  };

  activeSummaryTab.set(TabType.MySystems, {
    text: t('CreateAndManageSystems...'),
    showButton: true,
    buttonText: t('CreateSystem'),
    buttonAction: () => {
      props.openAction(CreateDialogType.System, props.currentSystem.projectId);
    },
    tableContent: tableItems.systems,
    tableElement: renderMySystems,
  });
  activeSummaryTab.set(TabType.MyProjects, {
    text: t('CreateAndManageProjects...'),
    showButton: true,
    buttonText: t('CreateProject'),
    buttonAction: () => {
      props.openAction(CreateDialogType.Project);
    },
    tableContent: tableItems.projects,
    tableElement: renderMyProjects,
  });
  activeSummaryTab.set(TabType.SystemsShared, {
    text: t('SystemsShared...'),
    showButton: false,
    buttonText: undefined,
    buttonAction: undefined,
    tableContent: tableItems.sharedSystems,
    tableElement: renderSharedSystems,
  });
  activeSummaryTab.set(TabType.ProjectsShared, {
    text: t('ProjectsShared...'),
    showButton: false,
    buttonText: undefined,
    buttonAction: undefined,
    tableContent: tableItems.sharedProjects,
    tableElement: renderSharedProjects,
  });

  const updateExpandedRowIndexAndClearRows = (resetRowIndices = false) => {
    const currentGridState = getCrossGridState();
    if (resetRowIndices) {
      updateCrossGridState({
        mySystemsExpandedRowIndex: undefined,
        sharedSystemsExpandedRowIndex: undefined,
        deletedProjects: currentGridState.deletedProjects,
      });
    }
    const detailProviderRef = selectedTab === TabType.MySystems ? mySystemsDetailProviderRef : sharedSystemsDetailProviderRef;
    const currentReference = detailProviderRef.current as unknown as WjRef<FlexGridDetailProvider>;
    if (!currentReference) {
      return;
    }
    const detailProviderControl = currentReference.control;
    const rowIndex = detailProviderControl.grid.selectedRows.length > 0 ? detailProviderControl.grid.selectedRows[0].index : undefined;
    if (rowIndex !== undefined && !resetRowIndices && detailProviderControl?.isDetailVisible(rowIndex)) {
      if (selectedTab === TabType.MySystems) {
        updateCrossGridState({
          mySystemsExpandedRowIndex: rowIndex,
          sharedSystemsExpandedRowIndex: currentGridState.sharedSystemsExpandedRowIndex,
          deletedProjects: currentGridState.deletedProjects,
        });
      } else if (selectedTab === TabType.SystemsShared) {
        updateCrossGridState({
          mySystemsExpandedRowIndex: currentGridState.mySystemsExpandedRowIndex,
          sharedSystemsExpandedRowIndex: rowIndex,
          deletedProjects: currentGridState.deletedProjects,
        });
      }
    }
    // For grids where rows have details (i.e. nested grid), the FlexGrid control will issue a
    // deferred update due to a bind operation (itemsSource seems to be changing to null) when
    // switching between tabs. The (re)bind triggers a loadingRows event which in turn triggers
    // a call to hideDetail() in the detail provider. For some reason, the Shared Systems grid
    // throws an error even if hideDetail() has been called programmatically before getting
    // called again by the (re)bind. The error occurs because hideDetail() tries to destroy the
    // detail row (cell) which in turn tries to find the detail cell's corresponding row in the
    // internal _renderedCells array which fails because the detail row in no longer rendered
    // at the time of the check.
    // Therefore, just before switching tabs, clearing the current grid's rows will avoid this
    // issue (the loop inside hideDetail() becomes moot) and is an "acceptable" workaround since
    // the grid will be replaced shortly and when switching back it will rebind to the correct
    // source/collection of items which will regenerate the corresponding rows.
    detailProviderControl.grid.rows.clear();
  };

  const removeSystemsOfDeletedProjects = () => {
    const crossGridState = getCrossGridState();
    if (crossGridState.deletedProjects.length < 1) return;

    const currentReference = mySystemsDetailProviderRef.current as unknown as WjRef<FlexGridDetailProvider>;
    const detailProviderControl = currentReference?.control;
    if (!detailProviderControl) {
      return;
    }
    const collectionView = detailProviderControl.grid.collectionView as CollectionView;
    for (const projectName of crossGridState.deletedProjects) {
      for (const rowData of collectionView.sourceCollection) {
        if (rowData.projectName === projectName) {
          collectionView.remove(rowData);
        }
      }
    }

    const newCrossGridState = { ...getCrossGridState() };
    newCrossGridState.deletedProjects = [];
    updateCrossGridState(newCrossGridState);
  };

  const addRecentlyCreatedProject = () => {
    ProjectService.getRecentlyCreatedProject(setProjectToAdd);
    if (!projectToAdd) return;

    const newProject = {
      ...projectToAdd,
      createdAtDate: new Date(),
      updatedAtDate: new Date(),
    };
    const currentReference = myProjectsGridRef.current;
    if (!currentReference) {
      return;
    }
    const collectionView = currentReference.collectionView as CollectionView;
    collectionView.sourceCollection.unshift(newProject);
    collectionView.refresh();
  };

  const renderPageData = () => {
    let collectionView: CollectionView | undefined;

    switch (selectedTab) {
      case TabType.MySystems: {
        collectionView = mySystemPageData;
        break;
      }
      case TabType.MyProjects: {
        collectionView = myProjectPageData;
        break;
      }
      case TabType.SystemsShared: {
        collectionView = sharedSystemPageData;
        break;
      }
      case TabType.ProjectsShared: {
        collectionView = sharedProjectPageData;
        break;
      }
    }

    if (showPagination(collectionView)) {
      return (
        <div className={styles['modal-pagination-div']}>
          <CollectionViewNavigator headerFormat={t('CollectionViewNavigatorHeaderFormat')} byPage={true} cv={collectionView} />
        </div>
      );
    }
  };

  useEffect(() => {
    addRecentlyCreatedProject();
  }, [projectToAdd]);

  return (
    <>
      <Modal
        disableEnforceFocus
        open={props.showSystemsProjectsModal}
        onClose={() => {
          updateExpandedRowIndexAndClearRows(true);
          props.closeAction();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className={styles['systems-projects-summary-container']}>
          {selectedTab === TabType.MySystems && renderMySystemMenuItems()}
          {selectedTab === TabType.MyProjects && renderMyProjectMenuItems()}
          {/*title*/}
          <div className={styles['modal-title-div']}>
            <div className={styles['modal-tabs']}>
              <div
                className={styles[`${selectedTab == TabType.MyProjects ? 'active-tab' : ''}`]}
                onClick={() => {
                  if (selectedTab == TabType.MyProjects) {
                    return;
                  }
                  updateExpandedRowIndexAndClearRows();
                  setSelectedTab(TabType.MyProjects);
                }}
              >
                {t('MyProjects')}
              </div>
              <div
                className={styles[`${selectedTab == TabType.MySystems ? 'active-tab' : ''}`]}
                onClick={() => {
                  if (selectedTab == TabType.MySystems) {
                    return;
                  }
                  updateExpandedRowIndexAndClearRows();
                  setSelectedTab(TabType.MySystems);
                  setTimeout(() => removeSystemsOfDeletedProjects(), 100);
                }}
              >
                {t('MySystems')}
              </div>

              <div
                className={styles[`${selectedTab == TabType.ProjectsShared ? 'active-tab' : ''}`]}
                onClick={() => {
                  if (selectedTab == TabType.ProjectsShared) {
                    return;
                  }
                  updateExpandedRowIndexAndClearRows();
                  setSelectedTab(TabType.ProjectsShared);
                }}
              >
                {t('ProjectsSharedWithMe')}
              </div>
              <div
                className={styles[`${selectedTab == TabType.SystemsShared ? 'active-tab' : ''}`]}
                onClick={() => {
                  if (selectedTab == TabType.SystemsShared) {
                    return;
                  }
                  updateExpandedRowIndexAndClearRows();
                  setSelectedTab(TabType.SystemsShared);
                }}
              >
                {t('SystemsSharedWithMe')}
              </div>
            </div>
            <div className={styles['x-mark']}>
              <FontAwesomeIcon
                icon="xmark"
                onClick={() => {
                  updateExpandedRowIndexAndClearRows(true);
                  props.closeAction();
                }}
              />
            </div>
          </div>
          {/*desc text*/}
          <div className={styles['modal-descriptive-text-wrapper']}>
            <div className={styles['modal-descriptive-text-div']}>{activeSummaryTab.get(selectedTab).text}</div>
            <div className={styles['modal-buttons-div']}>
              {activeSummaryTab.get(selectedTab).showButton && (
                <Button
                  onClick={() => {
                    activeSummaryTab.get(selectedTab).buttonAction();
                  }}
                >
                  {activeSummaryTab.get(selectedTab).buttonText}
                </Button>
              )}
            </div>
          </div>
          {/*table*/}
          <div className={styles['modal-table-div']}>
            {showEditDialog && selectedRowData.name && !isContextMenuOpen && renderEditProjectSystemDialog()}
            {activeSummaryTab.get(selectedTab).tableElement}
          </div>
          {/*pagination*/}
          {renderPageData()}
        </div>
      </Modal>
      {shareSystemDialogProps && renderShareSystemDialog()}
      {shareProjectDialogProps && renderShareProjectDialog()}
    </>
  );
};
